// @givmo/fundraisers-module — what a campaign calls the campaigns inside it (FCM-WEB59; D-FCM-50, and the card
// Jonathan ruled with D-FCM-52, 2026-10-22 07:19 PDT; `scratchpad/2026-10-19-fcm-web59-level-name-mockups/`):
// FCM-BE100's `layer_name`, singular and plural, written by the owner-agnostic settings write. It sits on the
// Layers tab under the tree that lists those campaigns (decision 1), shaped as the module's setting cards
// (`campaign-settings-cards.tsx`): its two fields, Save at its foot, never a save on blur (decision 2), no
// preview (decision 3), and the server's refusal in the module's toast (decision 4).
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sdk, type P2pConfigOut } from '@givmo/api-client';
import { Button, Card, CardBody, CardHeader, TextField, toast } from '@givmo/design-system';
import { LAYERS_COPY as C } from './layers-copy';
import {
  LAYER_NAME_MAX_LENGTH,
  collapseLayerName,
  layerNameBody,
  layerRefusal,
  storedLayerName,
} from './layers-decode';
import { useLivePage } from './use-live-page';

/**
 * The name this campaign's pages use for the campaigns inside it. Empty, they say "Campaign" and
 * "Campaigns", which each field shows as its placeholder. Each level's own organizer names its own children
 * (D-FCM-50), at the rank that changes its layers. The two forms go together, so one Save sends both: the
 * server refuses one without the other, and "Team(s)" on a campaign with built-in teams, in its own words,
 * and the fields keep what was typed. Without that rank it reads as stored, with no fields and no Save
 * (tester-5's item 16).
 */
export function LayerNameCard({
  campaignId,
  config,
  editable,
  onSaved,
}: {
  campaignId: string;
  config: P2pConfigOut;
  /** The rank that changes this campaign's layers, on a campaign that hasn't ended. */
  editable: boolean;
  onSaved: () => Promise<unknown>;
}) {
  const live = useLivePage();
  const stored = storedLayerName(config);
  const storedSingular = stored?.singular ?? '';
  const storedPlural = stored?.plural ?? '';
  const [singular, setSingular] = useState(storedSingular);
  const [plural, setPlural] = useState(storedPlural);
  useEffect(() => {
    setSingular(storedSingular);
    setPlural(storedPlural);
  }, [storedSingular, storedPlural]);
  const changed =
    collapseLayerName(singular) !== storedSingular || collapseLayerName(plural) !== storedPlural;
  const save = useMutation({
    mutationFn: () =>
      sdk.campaignsUpdateCampaign({
        path: { fundraiser_id: campaignId },
        body: layerNameBody(singular, plural),
        throwOnError: true,
      }),
    onSuccess: async () => {
      await onSaved();
      if (live()) toast.success(C.layerNameSaved);
    },
    onError: (error) => {
      if (live()) toast.error(layerRefusal(error).message);
    },
  });
  return (
    <Card flush testId="layers-level-name-card">
      <CardHeader title={C.layerNameTitle} subtitle={C.layerNameSubtitle} />
      <CardBody className="flex flex-col gap-16">
        {editable ? (
          <>
            <div className="grid gap-12 sm:max-w-[536px] sm:grid-cols-2">
              <TextField
                label={C.layerNameSingular}
                placeholder={C.layerNameSingularDefault}
                value={singular}
                onChange={(event) => setSingular(event.target.value)}
                maxLength={LAYER_NAME_MAX_LENGTH}
                autoComplete="off"
                disabled={save.isPending}
                testId="layers-level-name-singular"
              />
              <TextField
                label={C.layerNamePlural}
                placeholder={C.layerNamePluralDefault}
                value={plural}
                onChange={(event) => setPlural(event.target.value)}
                maxLength={LAYER_NAME_MAX_LENGTH}
                autoComplete="off"
                disabled={save.isPending}
                testId="layers-level-name-plural"
              />
            </div>
            <p className="text-s12 leading-h15 text-text-secondary">{C.layerNameEmptyMeans}</p>
            <Button
              appearance="saas"
              tone="secondary"
              className="self-end"
              disabled={!changed || save.isPending}
              loading={save.isPending}
              onClick={() => save.mutate()}
              testId="layers-level-name-save"
            >
              {C.layerNameSave}
            </Button>
          </>
        ) : (
          <dl
            className="grid gap-x-16 gap-y-10 text-s13 sm:grid-cols-[max-content_1fr]"
            data-testid="layers-level-name-read"
          >
            {(
              [
                [C.layerNameSingular, storedSingular || C.layerNameSingularDefault],
                [C.layerNamePlural, storedPlural || C.layerNamePluralDefault],
              ] as const
            ).map(([term, value]) => (
              <div key={term} className="contents">
                <dt className="text-text-secondary">{term}</dt>
                <dd className="font-w600 text-text-strong">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardBody>
    </Card>
  );
}
